import { useWatch } from 'react-hook-form';
import React, { useMemo } from 'react';
import type { InputTableCellParams, InputTableRowParams, RepeatRowsPayload } from '@/components/custom/erp';
import type { PasteResolverConfig, PhieuThuDetailRow, ReceiptCellValue } from '../../../../types';
import { getReceiptVoucherDetailColumns } from '../../../../cols-definition';
import { useInvoiceHydration } from '@/hooks/queries/useInvoiceHydration';
import { type SelectedCellInfo, useDetailPaste } from '../../../../hooks';
import InputTableActionBar from './InputTableActionBar';
import { InputTable } from '@/components/custom/erp';
import { copyCellValue } from '@/lib/table-handlers';
import { FormMode } from '@/types/form';

interface DetailsTabProps {
  rows: PhieuThuDetailRow[];
  selectedRowUuid: string | null;
  selectedCell?: SelectedCellInfo | null;
  handleRowClick: (params: InputTableRowParams<PhieuThuDetailRow>) => void;
  handleCellClick?: (params: InputTableCellParams) => void;
  handleAddRow: () => void;
  handleDeleteRow: () => void;
  handleCopyRow: () => void;
  handlePasteRow: (clipboardText?: string) => void | Promise<void>;
  handleMoveRow: (direction: 'up' | 'down') => void;
  handleCellValueChange: (rowUuid: string, field: string, newValue: ReceiptCellValue) => void;
  onRepeatForRows: (payload: RepeatRowsPayload<PhieuThuDetailRow>) => void;
  formMode: FormMode;
  handleUpdateNonWritableFields?: (fields: Set<string>) => void;
  handleUpdatePasteConfigs?: (configs: Record<string, PasteResolverConfig>) => void;
  isPasting?: boolean;
}

export const DetailsTab: React.FC<DetailsTabProps> = ({
  rows,
  selectedRowUuid,
  selectedCell,
  handleRowClick,
  handleCellClick,
  handleAddRow,
  handleDeleteRow,
  handleCopyRow,
  handlePasteRow,
  handleMoveRow,
  handleCellValueChange,
  onRepeatForRows,
  formMode,
  handleUpdateNonWritableFields,
  handleUpdatePasteConfigs,
  isPasting
}) => {
  const [ma_ngv, dien_giai] = useWatch({ name: ['ma_ngv', 'dien_giai'] });
  useInvoiceHydration({
    rows,
    enabled: formMode !== 'add',
    onHydrated: (uuid, invoice) => handleCellValueChange(uuid, 'id_hd_data', invoice)
  });
  const columns = useMemo(
    () => getReceiptVoucherDetailColumns(handleCellValueChange, ma_ngv, dien_giai, formMode),
    [handleCellValueChange, ma_ngv, dien_giai, formMode]
  );

  useDetailPaste({
    columns,
    handleUpdateNonWritableFields,
    handleUpdatePasteConfigs
  });

  return (
    <InputTable
      rows={rows}
      autoHeight
      bottomSelector='[data-arito-bottom-bar]'
      onRowClick={handleRowClick}
      onCellClick={handleCellClick}
      selectedRowId={selectedRowUuid || undefined}
      columns={columns}
      getRowId={row => row?.uuid || ''}
      enableContextMenu
      onCopyData={({ columnValue, columnField, columnValues }) => {
        copyCellValue({
          columnField,
          columnValue,
          columnValues,
          fallback: text => console.log('Copy data per column:', text)
        });
      }}
      onRepeatForRows={onRepeatForRows}
      actionButtons={
        <InputTableActionBar
          mode={formMode}
          handleAddRow={handleAddRow}
          handleDeleteRow={handleDeleteRow}
          handleCopyRow={handleCopyRow}
          handlePasteRow={handlePasteRow}
          selectedCell={selectedCell}
          handleMoveRow={handleMoveRow}
          isPasting={isPasting}
        />
      }
    />
  );
};
